import { useCallback, useEffect, useRef, useState } from "react";

import * as service from "../services/notificationService";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const normalizeCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
};

export const useNotifications = (enabled = true) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const unreadIdsRef = useRef(new Set());
  const pendingIdsRef = useRef(new Set());
  const markAllPendingRef = useRef(false);

  const versionRef = useRef(0);
  const countRequestRef = useRef(0);
  const listRequestRef = useRef(0);

  const refreshUnreadCount = useCallback(async () => {
    if (!enabled) return false;

    const requestId = ++countRequestRef.current;
    const version = versionRef.current;

    try {
      const { data } = await service.getUnreadCount();

      if (data?.success === false) {
        throw new Error("Failed to load unread count.");
      }

      const count = normalizeCount(data?.unreadCount);

      if (
        requestId === countRequestRef.current &&
        version === versionRef.current
      ) {
        setUnreadCount(count);
      }

      return count;
    } catch (err) {
      console.error("Failed to load unread count:", err);
      return false;
    }
  }, [enabled]);

  const getNotifications = useCallback(async () => {
    if (!enabled) return false;

    const requestId = ++listRequestRef.current;
    const version = versionRef.current;

    setLoading(true);
    setError("");

    try {
      const { data } = await service.getNotifications(20, 0);

      if (!data?.success) {
        throw new Error("Failed to load notifications.");
      }

      const items = Array.isArray(data.notifications)
        ? data.notifications.map((item) => ({
            ...item,
            isRead: Number(item.isRead) === 1 ? 1 : 0,
          }))
        : [];

      if (
        requestId !== listRequestRef.current ||
        version !== versionRef.current
      ) {
        return false;
      }

      unreadIdsRef.current = new Set(
        items
          .filter((item) => item.isRead === 0)
          .map((item) => String(item.notificationId)),
      );

      setNotifications(items);

      if (data.unreadCount !== undefined) {
        countRequestRef.current += 1;
        setUnreadCount(normalizeCount(data.unreadCount));
      }

      return items;
    } catch (err) {
      if (
        requestId === listRequestRef.current &&
        version === versionRef.current
      ) {
        setError(getErrorMessage(err, "Failed to load notifications."));
      }

      return false;
    } finally {
      if (requestId === listRequestRef.current) {
        setLoading(false);
      }
    }
  }, [enabled]);

  const markAsRead = useCallback(
    async (notificationId) => {
      const id = Number(notificationId);

      if (
        !enabled ||
        !Number.isSafeInteger(id) ||
        id <= 0 ||
        markAllPendingRef.current
      ) {
        return false;
      }

      const key = String(id);

      if (pendingIdsRef.current.has(key)) {
        return false;
      }

      pendingIdsRef.current.add(key);

      try {
        setError("");

        const { data } = await service.markNotificationAsRead(id);

        if (!data?.success) {
          throw new Error("Failed to mark notification as read.");
        }

        versionRef.current += 1;
        countRequestRef.current += 1;

        const wasUnread = unreadIdsRef.current.has(key);

        unreadIdsRef.current.delete(key);

        setNotifications((previous) =>
          previous.map((item) =>
            Number(item.notificationId) === id ? { ...item, isRead: 1 } : item,
          ),
        );

        if (wasUnread) {
          setUnreadCount((previous) => Math.max(0, previous - 1));
        }

        void refreshUnreadCount();

        return true;
      } catch (err) {
        setError(getErrorMessage(err, "Failed to mark notification as read."));

        return false;
      } finally {
        pendingIdsRef.current.delete(key);
      }
    },
    [enabled, refreshUnreadCount],
  );

  const markAllAsRead = useCallback(async () => {
    if (
      !enabled ||
      markAllPendingRef.current ||
      pendingIdsRef.current.size > 0
    ) {
      return false;
    }

    markAllPendingRef.current = true;

    try {
      setError("");

      const { data } = await service.markAllNotificationsAsRead();

      if (!data?.success) {
        throw new Error("Failed to mark all notifications as read.");
      }

      versionRef.current += 1;
      countRequestRef.current += 1;

      unreadIdsRef.current.clear();

      setNotifications((previous) =>
        previous.map((item) => ({
          ...item,
          isRead: 1,
        })),
      );

      setUnreadCount(0);

      void refreshUnreadCount();

      return true;
    } catch (err) {
      setError(
        getErrorMessage(err, "Failed to mark all notifications as read."),
      );

      return false;
    } finally {
      markAllPendingRef.current = false;
    }
  }, [enabled, refreshUnreadCount]);

  useEffect(() => {
    if (!enabled) {
      versionRef.current += 1;
      countRequestRef.current += 1;
      listRequestRef.current += 1;

      unreadIdsRef.current.clear();
      pendingIdsRef.current.clear();

      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      setError("");

      return;
    }

    void refreshUnreadCount();

    const intervalId = window.setInterval(() => {
      void refreshUnreadCount();
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
      versionRef.current += 1;
      countRequestRef.current += 1;
      listRequestRef.current += 1;
    };
  }, [enabled, refreshUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    getNotifications,
    refreshUnreadCount,
    markAsRead,
    markAllAsRead,
  };
};

export default useNotifications;
